/**
 * Device-side waiting-time metrics (feature 334, US3, research D8).
 *
 * The device times its own main requests and reports small one-minute
 * aggregates, so the backend can warn about a slow venue. Rules:
 *  - `record` adds one sample to the aggregate of the current minute and class;
 *  - a flush sends ONE report with whatever completed in earlier minutes;
 *  - nothing is queued: the report is built, handed to the sender once and
 *    forgotten, whether the send works or not (a weak line gets no extra load);
 *  - an empty window sends nothing; a send still in flight makes the next one
 *    drop instead of piling up.
 *
 * Pure TypeScript, no React: the copy under kitchen/expo-app/src/lib/sync must
 * stay byte for byte identical. Clock, timers and sender are injected.
 */

export type MetricClass = "orders_sync" | "config";

/** Upper bounds (ms) of the first five buckets; the sixth is open (> 10 s). */
export const HIST_BOUNDS_MS = [500, 1000, 2000, 5000, 10000] as const;
export const HIST_SIZE = HIST_BOUNDS_MS.length + 1;
export const FLUSH_INTERVAL_MS = 15_000;
const MINUTE_MS = 60_000;

export interface ClassReport {
  count: number;
  fail: number;
  sumMs: number;
  maxMs: number;
  hist: number[];
}

export interface MetricsReport {
  /** Epoch minutes of the device clock. */
  minute: number;
  classes: Partial<Record<MetricClass, ClassReport>>;
}

export interface MetricsOptions {
  /** Sends one report. Rejections are swallowed; the report is never retried. */
  send: (report: MetricsReport) => Promise<unknown>;
  now?: () => number;
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (id: unknown) => void;
  intervalMs?: number;
}

export interface Metrics {
  record(cls: MetricClass, durationMs: number, ok: boolean): void;
  /** Times `fn`; a rejection counts as a failure and is rethrown. */
  measure<T>(cls: MetricClass, fn: () => Promise<T>): Promise<T>;
  /** Sends the completed minutes now (the timer calls this). */
  flush(): void;
  /** Starts the periodic flush (shared by callers); returns the release. */
  start(): () => void;
}

export const bucketIndex = (ms: number): number => {
  const i = HIST_BOUNDS_MS.findIndex((bound) => ms <= bound);
  return i === -1 ? HIST_SIZE - 1 : i;
};

const emptyClass = (): ClassReport => ({ count: 0, fail: 0, sumMs: 0, maxMs: 0, hist: new Array(HIST_SIZE).fill(0) });

export function createMetrics(options: MetricsOptions): Metrics {
  const now = options.now ?? Date.now;
  const setI = options.setInterval ?? ((fn, ms) => setInterval(fn, ms));
  const clearI = options.clearInterval ?? ((id) => clearInterval(id as ReturnType<typeof setInterval>));
  const buckets = new Map<number, Partial<Record<MetricClass, ClassReport>>>();
  let sending = false;
  let users = 0;
  let timer: unknown;

  const currentMinute = () => Math.floor(now() / MINUTE_MS);

  function record(cls: MetricClass, durationMs: number, ok: boolean): void {
    const minute = currentMinute();
    // Nothing is kept beyond the last completed minute, even if no flush runs.
    for (const m of buckets.keys()) if (m < minute - 1) buckets.delete(m);
    const duration = Math.max(0, Math.round(durationMs));
    const bucket = buckets.get(minute) ?? {};
    buckets.set(minute, bucket);
    const agg = (bucket[cls] ??= emptyClass());
    agg.count += 1;
    if (!ok) agg.fail += 1;
    agg.sumMs += duration;
    agg.maxMs = Math.max(agg.maxMs, duration);
    agg.hist[bucketIndex(duration)] += 1;
  }

  async function measure<T>(cls: MetricClass, fn: () => Promise<T>): Promise<T> {
    const startedAt = now();
    try {
      const value = await fn();
      record(cls, now() - startedAt, true);
      return value;
    } catch (error) {
      record(cls, now() - startedAt, false);
      throw error;
    }
  }

  function flush(): void {
    const minute = currentMinute();
    const done = [...buckets.keys()].filter((m) => m < minute).sort((a, b) => a - b);
    if (done.length === 0) return;
    const classes: MetricsReport["classes"] = {};
    for (const m of done) {
      const bucket = buckets.get(m)!;
      buckets.delete(m);
      for (const cls of Object.keys(bucket) as MetricClass[]) {
        const from = bucket[cls]!;
        const into = (classes[cls] ??= emptyClass());
        into.count += from.count;
        into.fail += from.fail;
        into.sumMs += from.sumMs;
        into.maxMs = Math.max(into.maxMs, from.maxMs);
        from.hist.forEach((n, i) => (into.hist[i] += n));
      }
    }
    if (sending) return; // the previous report is still travelling: drop this one
    sending = true;
    let result: Promise<unknown>;
    try {
      result = Promise.resolve(options.send({ minute: done[done.length - 1], classes }));
    } catch {
      sending = false;
      return;
    }
    result.then(
      () => undefined,
      () => undefined,
    ).then(() => {
      sending = false;
    });
  }

  function start(): () => void {
    users += 1;
    if (users === 1) timer = setI(flush, options.intervalMs ?? FLUSH_INTERVAL_MS);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      users -= 1;
      if (users === 0) {
        clearI(timer);
        timer = undefined;
        buckets.clear();
      }
    };
  }

  return { record, measure, flush, start };
}
